import { Injectable } from '@angular/core';
interface IFile {
  name: string;
  contentType: string;
  publicLink: string;
  type: string;
  displayName: string;
}
@Injectable({
  providedIn: 'root',
})
export class FileTypeService {
  public isVideoFile(file: Partial<IFile>): boolean {
    if (file) {
      if (file.contentType?.startsWith('video')) {
        return true;
      }
      const videoExtensions: string[] = ['mp4', 'webm', 'mkv', 'flv', 'mov', 'hevc'];
      const endsWithVideoExtension: (str: string) => boolean = (str: string) =>
        videoExtensions.some((ext: string) => str.toLowerCase().endsWith(`.${ext}`));

      if (file.type && typeof file.type === 'string' && endsWithVideoExtension(file.type)) {
        return true;
      } else if (file.contentType && typeof file.contentType === 'string' && endsWithVideoExtension(file.contentType)) {
        return true;
      } else if (file.publicLink && endsWithVideoExtension(file.publicLink)) {
        return true;
      } else if (file.name && endsWithVideoExtension(file.name)) {
        return true;
      }
    }
    return false;
  }

  public isAudioFile(file: Partial<IFile>): boolean {
    if (file) {
      if (file.contentType?.startsWith('audio')) {
        return true;
      }
      const audioExtensions: string[] = ['mp3', 'ogg', 'wav'];
      const endsWithAudioExtension: (str: string) => boolean = (str: string) =>
        audioExtensions.some((ext: string) => str.toLowerCase().endsWith(`.${ext}`));

      if (file.type && typeof file.type === 'string' && endsWithAudioExtension(file.type)) {
        return true;
      } else if (file.contentType && typeof file.contentType === 'string' && endsWithAudioExtension(file.contentType)) {
        return true;
      } else if (file.publicLink && endsWithAudioExtension(file.publicLink)) {
        return true;
      } else if (file.name && endsWithAudioExtension(file.name)) {
        return true;
      }
    }
    return false;
  }

  public isAudioFileByExtension(extension: string): boolean {
    return extension.startsWith('audio');
  }

  public isSupportedImageFormat(mimeType: string): boolean {
    switch (mimeType) {
      case 'image/png':
      case 'image/jpeg':
      case 'image/gif':
      case 'image/bmp':
        return true;
      default:
        return false;
    }
  }

  public isImageFile(file: Partial<IFile>): boolean {
    if (file) {
      const imageRegex: RegExp = /\.(png|jpg|jpeg|bmp|gif|tif|tiff|psd|psb)$/i;
      if (
        file.contentType?.startsWith('image') ||
        (file.type && typeof file.type === 'string' && imageRegex.test(file.type)) ||
        (file.contentType && typeof file.contentType === 'string' && imageRegex.test(file.contentType)) ||
        (file.publicLink && typeof file.publicLink === 'string' && imageRegex.test(file.publicLink)) ||
        (file.name && imageRegex.test(file.name.toLowerCase()))
      ) {
        return true;
      }
    }
    return false;
  }

  public isImageFileByExtension(extension: string): boolean {
    return extension.startsWith('image');
  }

  public isSubtitleFile(file: Partial<IFile>): boolean {
    if (file && (file.name || file.displayName)) {
      if ((file.name || file.displayName).toLowerCase().endsWith('.srt')) {
        return true;
      }
    }
    return false;
  }

  public isMediaFile(file: Partial<IFile>): boolean {
    return this.isAudioFile(file) || this.isVideoFile(file);
  }

  public isOfficeMimeType(extension: string): boolean {
    const mimeTypes: string[] = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.presentation',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.text',
    ];

    return mimeTypes.includes(extension);
  }

  public isPowerPointMimeType(extension: string): boolean {
    const mimeTypes: string[] = [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ];
    return mimeTypes.includes(extension);
  }

  public isWordMimeType(extension: string): boolean {
    const mimeTypes: string[] = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    return mimeTypes.includes(extension);
  }

  public isExcelMimeType(extension: string): boolean {
    const mimeTypes: string[] = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    return mimeTypes.includes(extension);
  }

  public isOpenDocumentMimeType(extension: string): boolean {
    const mimeTypes: string[] = [
      'application/vnd.oasis.opendocument.presentation',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.text',
    ];
    return mimeTypes.includes(extension);
  }

  public isKeynoteFile(file: Partial<IFile>): boolean {
    const mimeTypes: string[] = ['application/x-iwork-keynote-sffkey', 'application/vnd.apple.keynote'];
    if (file.contentType) {
      return mimeTypes.includes(file.contentType);
    }
    if (file.type) {
      return mimeTypes.includes(file.type);
    }
    return false;
  }

  public isOfficeDoc(file: Partial<IFile>): boolean {
    const embedTypes: string[] = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.presentation',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.text',
      'application/wps-office.xlsx',
      'application/wps-office.docx',
      'application/wps-office.pptx',
    ];

    if (!file) return false;

    if (file.contentType && embedTypes.find((k) => k == file.contentType)) return true;
    else return !!(file.type && embedTypes.find((k) => k == file.type));
  }

  public isOfficeDocWithoutPpt(file: Partial<IFile>): boolean {
    if (!file) return false;

    const mimeTypes: string[] = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.presentation',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.text',
      'application/wps-office.xlsx',
      'application/wps-office.docx',
    ];

    if (file.contentType) {
      return mimeTypes.includes(file.contentType);
    }
    if (file.type) {
      return mimeTypes.includes(file.type);
    }
    return false;
  }

  public isPowerPointFile(file: Partial<IFile>): boolean {
    if (!file) return false;

    const mimeTypes: string[] = [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/wps-office.pptx',
    ];

    if (file.contentType) {
      return mimeTypes.includes(file.contentType);
    }
    if (file.type) {
      return mimeTypes.includes(file.type);
    }
    return false;
  }

  public isConvertibleImageFormat(file: Partial<IFile>): boolean {
    const contentTypes: string[] = [
      'image/tiff',
      'image/vnd.adobe.photoshop',
      'image/x-photoshop',
      'application/vnd.3gpp.pic-bw-small',
    ];

    if (!file) return false;

    if (file.contentType && contentTypes.find((k) => k == file.contentType)) return true;
    else return !!(file.type && contentTypes.find((k) => k == file.type));
  }

  public isCsvFile(file: Partial<IFile>): boolean {
    const contentTypes: string[] = ['text/csv'];

    if (!file) return false;

    if (file.contentType && contentTypes.find((k) => k == file.contentType)) return true;
    else return !!(file.type && contentTypes.find((k) => k == file.type));
  }

  public isObjFile(file: Partial<IFile>): boolean {
    const contentTypes: string[] = ['text/plain'];
    if (!file) return false;
    if (
      file.contentType &&
      contentTypes.find((k) => k == file.contentType) &&
      this.isFileExtension(file.displayName, 'obj')
    )
      return true;
  }

  public isTextFile(file: Partial<IFile>): boolean {
    const contentTypes: string[] = ['text/plain', 'application/rtf'];

    if (!file) return false;

    if (file.contentType && contentTypes.find((k) => k == file.contentType)) return true;
    else return !!(file.type && contentTypes.find((k) => k == file.type));
  }

  public getVideoMimeType(file: { name?: string; publicLink?: string }): string {
    if (!file) {
      return 'video/mp4';
    }
    if (
      (file.publicLink && this.isFileExtension(file.publicLink, 'webm')) ||
      (file.name && this.isFileExtension(file.name, 'webm'))
    ) {
      return 'video/webm';
    }
    if (
      (file.publicLink && this.isFileExtension(file.publicLink, 'flv')) ||
      (file.name && this.isFileExtension(file.name, 'flv'))
    ) {
      return 'video/x-flv';
    }
    return 'video/mp4';
  }

  private isFileExtension(filename: string, extension: string): boolean {
    if (filename) return filename.toLowerCase().endsWith(extension.toLowerCase());
    return false;
  }
}
